/** One client key per intent: re-used by every retry, refresh and second tab (UX03, ruling 0.7). */
import type { CommandRequest } from "../contracts/commands.ts";

/** What the browser remembers about one intent: its identity, the exact request and its command. */
export interface IntentRecord {
  readonly clientKey: string;
  /** The request exactly as first sent, so a retry presents the same body under the same identity. */
  readonly request: CommandRequest | null;
  readonly commandId: string | null;
}

type Memory = Pick<Storage, "getItem" | "setItem" | "removeItem" | "clear">;

/** The part of `BroadcastChannel` the keys use; a second tab of the same session hears the first. */
export interface ChannelLike {
  postMessage(message: unknown): void;
  addEventListener(type: "message", listener: (event: MessageEvent<unknown>) => void): void;
  removeEventListener(type: "message", listener: (event: MessageEvent<unknown>) => void): void;
  close(): void;
}

export interface IntentKeysDeps {
  /** `sessionStorage`: survives a refresh, dies with the tab and is cleared at logout (UX06). */
  readonly storage: Memory;
  readonly channel: ChannelLike | null;
  readonly mint: () => string;
}

const PREFIX = "ssi.intent.";

function isRecord(value: unknown): value is IntentRecord {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const { clientKey, commandId, request } = value as Record<string, unknown>;
  return (
    typeof clientKey === "string" &&
    (commandId === null || typeof commandId === "string") &&
    (request === null || typeof request === "object")
  );
}

/** Keeps one `IntentRecord` per intent id in memory, in session storage and across tabs. */
export class IntentKeys {
  private readonly deps: IntentKeysDeps;
  private readonly known = new Map<string, IntentRecord>();
  private readonly listener = (event: MessageEvent<unknown>): void => {
    const message = event.data;
    if (typeof message !== "object" || message === null) {
      return;
    }
    const { intentId, record } = message as { intentId?: unknown; record?: unknown };
    if (typeof intentId !== "string") {
      return;
    }
    const held = this.known.get(intentId);
    if (record === null) {
      this.known.delete(intentId);
    } else if (
      isRecord(record) &&
      (held === undefined ||
        record.commandId !== null ||
        (held.request === null && record.request !== null && held.clientKey === record.clientKey))
    ) {
      // A request sent under the identity this tab holds is adopted, so a retry here re-sends
      // that exact body instead of building a different one under the same key (review F1).
      this.known.set(intentId, record);
    }
  };

  constructor(deps: IntentKeysDeps) {
    this.deps = deps;
    deps.channel?.addEventListener("message", this.listener);
  }

  private save(intentId: string, record: IntentRecord): void {
    this.known.set(intentId, record);
    try {
      this.deps.storage.setItem(`${PREFIX}${intentId}`, JSON.stringify(record));
    } catch {
      // Storage may be blocked; the memory copy and the other tabs still hold the identity.
    }
    this.deps.channel?.postMessage({ intentId, record });
  }

  /** The record of an intent if one exists (memory, then session storage); never mints. */
  find(intentId: string): IntentRecord | null {
    const held = this.known.get(intentId);
    if (held !== undefined) {
      return held;
    }
    try {
      const text = this.deps.storage.getItem(`${PREFIX}${intentId}`);
      const parsed: unknown = text === null ? null : JSON.parse(text);
      if (isRecord(parsed)) {
        this.known.set(intentId, parsed);
        return parsed;
      }
    } catch {
      // Unreadable storage is the same as none.
    }
    return null;
  }

  /** The record of an intent: the existing one, or a new identity minted now (once per intent). */
  open(intentId: string): IntentRecord {
    const found = this.find(intentId);
    if (found !== null) {
      return found;
    }
    const record: IntentRecord = { clientKey: this.deps.mint(), request: null, commandId: null };
    this.save(intentId, record);
    return record;
  }

  /** Keep the request exactly as sent, so any retry presents the same body. */
  keepRequest(intentId: string, request: CommandRequest): void {
    const record = this.open(intentId);
    this.save(intentId, { ...record, request });
  }

  /** Keep the durable command id the server answered with. */
  keepCommand(intentId: string, commandId: string): void {
    const record = this.open(intentId);
    this.save(intentId, { ...record, commandId });
  }

  /** Forget an intent so the next request is a reviewed new one with a new identity. */
  forget(intentId: string): void {
    this.known.delete(intentId);
    try {
      this.deps.storage.removeItem(`${PREFIX}${intentId}`);
    } catch {
      // Nothing to remove.
    }
    this.deps.channel?.postMessage({ intentId, record: null });
  }

  /** Drop every identity held in memory (logout or revocation); session storage is cleared by its owner. */
  clear(): void {
    this.known.clear();
  }

  /** Stop listening to other tabs. */
  dispose(): void {
    this.deps.channel?.removeEventListener("message", this.listener);
    this.deps.channel?.close();
    this.known.clear();
  }
}
